/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { DateRule, WorkflowField } from '@/api/fields';
import { longDate } from '@/lib/dates';

/**
 * The checks on an agreement's values, wherever they are typed.
 *
 * One definition, for the launch form that makes an agreement and the
 * agreement page that corrects one before it is sent again (lifecycle
 * §10.6.1). A second copy would drift from the first, and the two would stop
 * agreeing about what an agreement needs before its document can be made.
 */

/**
 * Keep the browser's saved details out of these fields.
 *
 * Every field here describes a **counterparty** — their name, the address of
 * the person who signs for them — and never the person filling it in. So an
 * address the browser has saved is wrong by construction, and an autofilled
 * one is worse than a blank: it arrives looking deliberately entered, and what
 * follows is a real signature request to whoever that was.
 *
 * `autocomplete="off"` alone is not enough — Chrome ignores it on a field it
 * decides is an email — so the name is made meaningless to the heuristics too,
 * and the two password managers with most of the market are told to stay out.
 */
export const NO_AUTOFILL = {
  autoComplete: 'off',
  // A name the heuristics recognise defeats the attribute above, so give them
  // one they do not.
  name: 'vellum-field',
  'data-1p-ignore': true,
  'data-lpignore': true,
} as const;

/**
 * Enough to catch a typo, not enough to argue with a real address.
 *
 * The server checks it properly. This is here so the mistake is caught while
 * the person is still looking at the field, rather than at the point of
 * sending — an envelope that goes to a mistyped address looks sent to us and
 * looks like nothing at all to them.
 */
/** The longest a counterparty's or a person's name may be, as the server says. */
export const NAME_MOST = 150;

/** The longest an email address may be, as the server says. */
export const EMAIL_MOST = 254;

/** From how far into a limit a field shows its count: the last fifth of it. */
const COUNT_SHOWN_FROM = 0.8;

/** Whether a field's count is worth showing yet, for this much typed of this limit. */
export function nearTheLimit(length: number, most: number): boolean {
  return length >= most * COUNT_SHOWN_FROM;
}

export const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The questions typed into a form: all but the counterparty, which is asked
 * once, at the top, and fills the field the workflow calls `counterparty`.
 */
export function askedFields(fields: readonly WorkflowField[]): WorkflowField[] {
  return fields.filter((field) => field.key !== 'counterparty');
}

/** The required questions still blank, by label — what the document cannot be made without. */
export function stillBlank(
  fields: readonly WorkflowField[],
  values: Readonly<Record<string, string>>,
): string[] {
  return fields
    .filter((field) => field.is_required && !(values[field.key] ?? '').trim())
    .map((field) => field.label);
}

/** The earliest date any question takes, as the server holds it. */
export const EARLIEST_DATE = '1900-01-01';

/** How far ahead of today any question's date may be, in years. */
export const YEARS_AHEAD = 50;

/** `23 October 2026`: a day as the refusals say it, without its weekday. */
function dayWords(iso: string): string {
  return longDate(iso).split(', ').pop() ?? iso;
}

/** The latest date any question takes: 50 years from today (29 February: the 28th). */
export function latestDate(today: string): string {
  const year = Number(today.slice(0, 4)) + YEARS_AHEAD;
  const rest = today.slice(4);
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return rest === '-02-29' && !leap ? `${String(year)}-02-28` : `${String(year)}${rest}`;
}

/** The first and last day a date question takes, for the input's `min` and `max`. */
export function dateBounds(rule: DateRule, today: string): { min: string; max: string } {
  return {
    min: rule === 'today_or_later' ? today : EARLIEST_DATE,
    max: rule === 'today_or_earlier' ? today : latestDate(today),
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * What is wrong with a date answer, in the words the server uses, or null.
 *
 * The same rule as the server's (`answer_rules.date_problem`): any sane date
 * -- from 1 January 1900 to 50 years from today -- and then its question's
 * rule about today, Amida's today. A blank is no answer, and fine.
 */
export function dateProblem(rule: DateRule, value: string, today: string): string | null {
  const day = value.trim();
  if (!day) return null;
  if (!ISO_DAY.test(day)) return 'Enter a date.';
  if (day < EARLIEST_DATE) return `A date from ${dayWords(EARLIEST_DATE)} on.`;
  const latest = latestDate(today);
  if (day > latest) {
    return `No later than ${dayWords(latest)}, ${String(YEARS_AHEAD)} years from today.`;
  }
  if (rule === 'today_or_earlier' && day > today) {
    return `Today (${dayWords(today)}) or earlier.`;
  }
  if (rule === 'today_or_later' && day < today)
    return `Today (${dayWords(today)}) or later.`;
  return null;
}

/** The questions whose answers break their date rule, by label: what Create waits on. */
export function outOfRange(
  fields: readonly WorkflowField[],
  values: Readonly<Record<string, string>>,
  today: string,
): string[] {
  return fields
    .filter(
      (field) =>
        field.field_type === 'date' &&
        dateProblem(field.date_rule, values[field.key] ?? '', today) !== null,
    )
    .map((field) => `a date in range for ${field.label}`);
}

/**
 * Whether an expiry asked on its own (VL-2041) can be sent: no expiry,
 * nothing yet, or a date in the range every date is.
 */
export function expiryReady(value: string, noExpiry: boolean, today: string): boolean {
  return noExpiry || dateProblem('any', value, today) === null;
}
